/** Difficulty levels for activity steps, easiest first. `level` is the stored and API value (the activity_level enum). */
export const ACTIVITY_LEVELS = [
  { level: 'easier', label: 'Easier' },
  { level: 'default', label: 'Default' },
  { level: 'harder', label: 'Harder' },
] as const;

export type ActivityLevel = (typeof ACTIVITY_LEVELS)[number]['level'];

/** What a child sees until a level is saved; the level a details screen opens on. */
export const DEFAULT_ACTIVITY_LEVEL: ActivityLevel = 'default';

export function isActivityLevel(value: string): value is ActivityLevel {
  return ACTIVITY_LEVELS.some((l) => l.level === value);
}

export function activityLevelLabel(level: ActivityLevel): string {
  return ACTIVITY_LEVELS.find((l) => l.level === level)!.label;
}

export const ACTIVITY_DESCRIPTION_MAX_LENGTH = 120;
/** Max length for whyItWorks, materialsNeeded and each step's text */
export const ACTIVITY_TEXT_MAX_LENGTH = 250;
/** Max steps per difficulty level */
export const ACTIVITY_MAX_STEPS_PER_LEVEL = 20;

export const ACTIVITY_STATUSES = ['draft', 'published', 'unpublished', 'archived'] as const;
export type ActivityStatusValue = (typeof ACTIVITY_STATUSES)[number];

export const ACTIVITY_STATUS_TRANSITIONS: Record<ActivityStatusValue, readonly ActivityStatusValue[]> = {
  draft: ['published', 'archived'],
  published: ['unpublished', 'archived'],
  unpublished: ['published', 'archived'],
  archived: [],
};

export interface ActivityRuleError {
  /** Same path the backend uses: 'title' | 'description' | 'whyItWorks' | 'domainId' | 'skillIds' | 'steps' | 'steps.{level}' */
  path: string;
  message: string;
}

export interface ActivityRuleInput {
  title?: string | null;
  description?: string | null;
  whyItWorks?: string | null;
  domainId?: number | null;
  skillIds: readonly number[];
  steps: ReadonlyArray<{ level: string; text: string }>;
}

export const activityStepsPath = (level: ActivityLevel) => `steps.${level}`;

const isBlank = (value: string | null | undefined) => !value || !value.trim();

/** The single rule set, applied on every save (create and edit, any status) and before publishing. */
export function getActivityRuleErrors(input: ActivityRuleInput): ActivityRuleError[] {
  const errors: ActivityRuleError[] = [];

  if (isBlank(input.title)) errors.push({ path: 'title', message: 'Title is required' });

  if (isBlank(input.description)) {
    errors.push({ path: 'description', message: 'Description is required' });
  } else if (input.description!.trim().length > ACTIVITY_DESCRIPTION_MAX_LENGTH) {
    errors.push({
      path: 'description',
      message: `Description must be ${ACTIVITY_DESCRIPTION_MAX_LENGTH} characters or less`,
    });
  }

  if (isBlank(input.whyItWorks)) errors.push({ path: 'whyItWorks', message: 'Why it works is required' });
  if (!input.domainId) errors.push({ path: 'domainId', message: 'Primary domain is required' });
  if (input.skillIds.length === 0) errors.push({ path: 'skillIds', message: 'At least one skill is required' });

  if (input.steps.some((p) => !isActivityLevel(p.level))) {
    errors.push({
      path: 'steps',
      message: `Level must be one of ${ACTIVITY_LEVELS.map((l) => l.level).join(', ')}`,
    });
  }

  for (const { level, label } of ACTIVITY_LEVELS) {
    const levelSteps = input.steps.filter((p) => p.level === level);
    if (levelSteps.length === 0) {
      errors.push({ path: activityStepsPath(level), message: `${label} needs at least one step` });
    } else if (levelSteps.length > ACTIVITY_MAX_STEPS_PER_LEVEL) {
      errors.push({
        path: activityStepsPath(level),
        message: `${label} allows at most ${ACTIVITY_MAX_STEPS_PER_LEVEL} steps`,
      });
    } else if (levelSteps.some((p) => isBlank(p.text))) {
      errors.push({ path: activityStepsPath(level), message: `${label} has an empty step` });
    }
  }

  return errors;
}
